import { Box, TextField, Button, Snackbar, Alert, Dialog, DialogContent, CircularProgress, Typography } from "@mui/material";
import React, { useState } from "react";
import { SimpleForm, useDataProvider } from "react-admin";
import { ManualReviewType, manualReviewTypeMetadata } from "../../constants/ManulReviewType";

const ManualReview: React.FC = () => {
  const dataProvider = useDataProvider();
  // 选择的认证类型，默认为身份证认证
  const [selectReviewType, setSelecteReviewType] = useState<ManualReviewType | ''>(ManualReviewType.IDCARD);

  // 身份证姓名
  const [idCardName, setIdCardName] = useState<string>('');
  // 身份证号
  const [idCardNumber, setIdCardNumber] = useState<string>('');
  // 手机号
  const [phoneNumber, setPhoneNumber] = useState<string>('');

  const handleReviewTypeChange = (type: ManualReviewType) => {
    setSelecteReviewType(type);
  }

  const handleIDCardNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIdCardName(event.target.value);
  }

  const handleIDCardNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIdCardNumber(event.target.value);
  }

  const handlePhoneNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPhoneNumber(event.target.value);
  }

  // 修改表单验证
  const isFormValid = () => {
    if (!selectReviewType) return false;
    if (!idCardName || !idCardNumber) return false;
    if (idCardNumber.length !== 18) return false;
    // 手机号验证（11位）
    if (selectReviewType === 'phone' && phoneNumber.length !== 11) return false;
    return true;
  };

  // 修改提交处理
  // 提示信息状态
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success'
  });

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // 添加加载状态
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!isFormValid()) return;

    setLoading(true); // 开始加载
    try {
      // 提交认证
      const response = await dataProvider.sendManualReview({
        type: selectReviewType,
        data: {
          name: idCardName,
          idNo: idCardNumber,
          phone: selectReviewType === ManualReviewType.PHONE ? phoneNumber : undefined
        }
      });

      // 提交成功后清空表单
      setIdCardName('');
      setIdCardNumber('');
      setPhoneNumber('');
      // 只有当服务器返回data为true时才显示成功提示
      setSnackbar({
        open: true,
        message: response.data.success ? '认证成功' : '认证失败，请稍后重试',
        severity: 'error'
      });
    } catch (error) {
      // 显示错误提示
      setSnackbar({
        open: true,
        message: '认证失败，请稍后重试',
        severity: 'error'
      });
    } finally {
      setLoading(false); // 结束加载
    }
  };

  return (
    <>
      <div style={{
        height: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
      }}>
        <SimpleForm
          toolbar={false}
          resource="driverSchedules"
          sx={{
            marginBottom: 0,
            padding: 2,
            width: "100%",
            maxWidth: "1200px",
            borderRadius: 1,
            boxShadow: 1
          }}
        >
          <Box sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            width: "100%",
            maxWidth: "800px",
            margin: "0 auto"
          }}>
            <Box sx={{ mb: 3 }}>
              {/* 认证类型选择Tabs */}
              <Box sx={{
                borderBottom: 1,
                borderColor: 'divider',
                mb: 2
              }}>
                {Object.entries(manualReviewTypeMetadata).map(([key, value]) => (
                  <Button
                    key={key}
                    sx={{
                      mr: 2,
                      px: 3,
                      py: 1,
                      borderRadius: '4px 4px 0 0',
                      borderBottom: selectReviewType === key ? 2 : 0,
                      borderColor: 'primary.main',
                      color: selectReviewType === key ? '#000' : 'text.primary',
                      backgroundColor: selectReviewType === key ? 'primary.main' : 'transparent',
                      fontWeight: selectReviewType === key ? 'bold' : 'normal',
                      '&:hover': {
                        backgroundColor: selectReviewType === key ? 'primary.dark' : 'action.hover',
                      }
                    }}
                    onClick={() => handleReviewTypeChange(key as ManualReviewType)}
                  >
                    {value.title}
                  </Button>
                ))}
              </Box>

              {/* 当前选择的认证类型说明 */}
              {selectReviewType && (
                <Box sx={{
                  backgroundColor: 'info.light',
                  p: 2,
                  borderRadius: 1,
                  color: 'info.contrastText'
                }}>
                  {manualReviewTypeMetadata[selectReviewType]?.subtitle}
                </Box>
              )}
            </Box>
            <TextField
              label="身份证姓名"
              variant="outlined"
              helperText="请填写身份证上的姓名(不能为空)"
              value={idCardName || ""}
              onChange={handleIDCardNameChange}
              fullWidth
            />
            <TextField
              label="身份证号码"
              variant="outlined"
              helperText={`请填写身份证上的号码(18位数字) - 当前输入${idCardNumber.length}个数字`}
              value={idCardNumber || ""}
              onChange={handleIDCardNumberChange}
              fullWidth
            />

            {/* 添加手机号输入框 */}
            {selectReviewType === 'phone' && (
              <TextField
                label="手机号"
                variant="outlined"
                helperText={`请填写需要认证的手机号(11位数字) - 当前输入${phoneNumber.length}个数字`}
                value={phoneNumber || ""}
                onChange={handlePhoneNumberChange}
                fullWidth
              />
            )}

            <Button
              variant="contained"
              color="primary"
              disabled={!isFormValid()}
              onClick={handleSubmit}
              sx={{ mt: 2 }}
            >
              提交认证
            </Button>
            {!selectReviewType && (
              <Box sx={{
                color: 'error.main',
                mt: 2,
                textAlign: 'center'
              }}>
                您还没有选择认证的类型，请选择后再继续。
              </Box>
            )}
          </Box>
        </SimpleForm>
      </div>

      {/* 提示信息弹窗 */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ top: '100px !important' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      
      {/* 加载中弹窗 */}
      <Dialog
        open={loading}
        disableEscapeKeyDown
        PaperProps={{
          sx: {
            backgroundColor: 'transparent',
            boxShadow: 'none',
            overflow: 'hidden'
          }
        }}
      >
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CircularProgress />
          <Typography sx={{ color: 'white' }}>正在认证...</Typography>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ManualReview;